<template>
  <div ref="map" class="destination-map"></div>
</template>

<script>
import GoogleMapsLoader from "google-maps";
import {
  getAreaColor,
  getAreaPath,
  getReservationPoints,
  formatShortPrice,
  buildPriceTagIcon,
  buildReservationInfoContent,
  buildAreaInfoContent,
  MIN_POLYGON_POINTS,
  DESTINATION_PIN_COLOR,
  ROUTE_LINE_COLOR,
  INFO_LABEL_STYLE
} from "../../services/map.service";

GoogleMapsLoader.KEY = process.env.VUE_APP_GOOGLE_MAPS_API_KEY;
GoogleMapsLoader.VERSION = 3.43;
if (!GoogleMapsLoader.LIBRARIES.includes("places")) {
  GoogleMapsLoader.LIBRARIES.push("places");
}

/** Zoom used when the map is centred on the branch. */
const BRANCH_ZOOM = 11;
/** Zoom used when there is no branch location. */
const WORLD_ZOOM = 2;
/** Border width of the coverage areas, in pixels. */
const AREA_STROKE_WEIGHT = 1;

/**
 * Google Maps view of a supplier destination: one polygon per coverage area and,
 * per reservation, a price tag at the origin, a pin at the destination and a line between them.
 * Overlays are redrawn on data changes; the map itself is created once.
 */
export default {
  name: "DestinationMap",
  props: {
    center: {
      type: Object,
      default: () => null,
      description: "Object with lat and lng used when there is nothing to fit"
    },
    areas: {
      type: Array,
      default: () => [],
      description: "Coverage areas with _id, code, description and polygon"
    },
    reservations: {
      type: Array,
      default: () => [],
      description: "Reservations returned by the branch map endpoint"
    },
    visibleAreaIds: {
      type: Array,
      default: () => [],
      description: "Ids of the areas to show"
    },
    showReservations: {
      type: Boolean,
      default: true,
      description: "Show or hide the reservations layer"
    }
  },
  created() {
    // Google Maps instances are kept out of `data` so Vue does not observe them.
    this.google = null;
    this.map = null;
    this.infoWindow = null;
    this.polygons = {};
    this.reservationOverlays = [];
  },
  watch: {
    areas() {
      this.drawAreas();
      this.fitBounds();
    },
    reservations() {
      this.drawReservations();
      this.fitBounds();
    },
    visibleAreaIds() {
      this.applyAreasVisibility();
    },
    showReservations() {
      this.applyReservationsVisibility();
    },
    center(currentValue) {
      if (this.map && currentValue && !this.hasOverlays()) {
        this.map.setCenter(currentValue);
      }
    }
  },
  mounted() {
    GoogleMapsLoader.load(google => {
      if (this._isDestroyed) return;
      this.initMap(google);
      this.drawAreas();
      this.drawReservations();
      this.fitBounds();
    });
  },
  beforeDestroy() {
    if (!this.google) return;
    this.clearAreas();
    this.clearReservations();
    this.infoWindow.close();
  },
  methods: {
    /* ------------------------------ Map ------------------------------ */

    /**
     * Create the map and the shared InfoWindow.
     * @param {Object} google - Google Maps namespace.
     */
    initMap(google) {
      this.google = google;
      this.map = new google.maps.Map(this.$refs.map, {
        zoom: this.center ? BRANCH_ZOOM : WORLD_ZOOM,
        center: this.center || { lat: 0, lng: 0 },
        scrollwheel: true,
        mapTypeId: google.maps.MapTypeId.ROADMAP
      });
      this.infoWindow = new google.maps.InfoWindow();
    },
    /**
     * Map an overlay should be attached to: the map when visible, null when hidden.
     * @param {boolean} visible
     * @returns {?Object}
     */
    getTargetMap(visible) {
      return visible ? this.map : null;
    },
    /**
     * Whether any area or reservation is drawn.
     * @returns {boolean}
     */
    hasOverlays() {
      return (
        Object.keys(this.polygons).length > 0 ||
        this.reservationOverlays.length > 0
      );
    },
    /**
     * Collect every area and reservation point into LatLngBounds.
     * @returns {{bounds: Object, count: number}}
     */
    buildBounds() {
      const bounds = new this.google.maps.LatLngBounds();
      const points = [
        ...this.areas.flatMap(getAreaPath),
        ...this.reservations.flatMap(reservation => {
          const { from, to } = getReservationPoints(reservation);
          return [from, to].filter(Boolean);
        })
      ];
      points.forEach(point => bounds.extend(point));
      return { bounds: bounds, count: points.length };
    },
    /**
     * Fit the map over all areas and reservations, or centre on the branch when empty.
     */
    fitBounds() {
      if (!this.map) return;
      const { bounds, count } = this.buildBounds();
      if (count > 0) {
        this.map.fitBounds(bounds);
      } else if (this.center) {
        this.map.setCenter(this.center);
        this.map.setZoom(BRANCH_ZOOM);
      }
    },

    /* ------------------------------ Areas ------------------------------ */

    /**
     * Draw one polygon per area, replacing the previous ones.
     */
    drawAreas() {
      if (!this.map) return;
      this.clearAreas();
      this.areas.forEach((area, index) => {
        const polygon = this.createAreaPolygon(area, getAreaColor(index));
        if (polygon) {
          this.polygons[area._id] = polygon;
        }
      });
    },
    /**
     * Create the polygon of an area with a click InfoWindow.
     * @param {Object} area - Area with _id, code, description and polygon.
     * @param {string} color - Stroke and fill colour.
     * @returns {?Object} Polygon, or null when the area has too few valid points.
     */
    createAreaPolygon(area, color) {
      const path = getAreaPath(area);
      if (path.length < MIN_POLYGON_POINTS) return null;
      const polygon = new this.google.maps.Polygon({
        paths: path,
        strokeColor: color,
        strokeOpacity: 0.8,
        strokeWeight: AREA_STROKE_WEIGHT,
        fillColor: color,
        fillOpacity: 0.2,
        map: this.getTargetMap(this.visibleAreaIds.includes(area._id))
      });
      polygon.addListener("click", event => {
        this.openInfoWindow(buildAreaInfoContent(area), {
          position: event.latLng
        });
      });
      return polygon;
    },
    /**
     * Show or hide each polygon according to `visibleAreaIds`.
     */
    applyAreasVisibility() {
      Object.keys(this.polygons).forEach(areaId => {
        this.polygons[areaId].setMap(
          this.getTargetMap(this.visibleAreaIds.includes(areaId))
        );
      });
    },
    /**
     * Remove every polygon and its listeners.
     */
    clearAreas() {
      Object.values(this.polygons).forEach(this.removeOverlay);
      this.polygons = {};
    },

    /* --------------------------- Reservations --------------------------- */

    /**
     * Draw every reservation, replacing the previous ones.
     */
    drawReservations() {
      if (!this.map) return;
      this.clearReservations();
      this.reservations.forEach(this.drawReservation);
    },
    /**
     * Draw the overlays of one reservation: origin tag, and destination pin + line when available.
     * Reservations without a valid origin are skipped.
     * @param {Object} reservation - Reservation returned by the branch map endpoint.
     */
    drawReservation(reservation) {
      const { from, to } = getReservationPoints(reservation);
      if (!from) return;
      const content = buildReservationInfoContent(reservation);
      this.reservationOverlays.push(
        this.createReservationMarker(from, this.createPriceTagIcon(reservation), reservation, content)
      );
      if (to) {
        this.reservationOverlays.push(
          this.createReservationMarker(to, this.createDestinationIcon(), reservation, content),
          this.createRouteLine(from, to)
        );
      }
    },
    /**
     * Icon of the origin: a price tag with the quoted price.
     * @param {Object} reservation
     * @returns {Object} Google Maps Icon.
     */
    createPriceTagIcon(reservation) {
      const tag = buildPriceTagIcon(formatShortPrice(reservation.quotedPrice));
      return {
        url: tag.url,
        scaledSize: new this.google.maps.Size(tag.width, tag.height),
        anchor: new this.google.maps.Point(tag.anchorX, tag.anchorY)
      };
    },
    /**
     * Icon of the destination: a green circle.
     * @returns {Object} Google Maps Symbol.
     */
    createDestinationIcon() {
      return {
        path: this.google.maps.SymbolPath.CIRCLE,
        scale: 6,
        fillColor: DESTINATION_PIN_COLOR,
        fillOpacity: 1,
        strokeColor: "#ffffff",
        strokeWeight: 1
      };
    },
    /**
     * Create a reservation marker that opens the reservation details on click.
     * @param {{lat: number, lng: number}} position - Marker position.
     * @param {Object} icon - Google Maps Icon or Symbol.
     * @param {Object} reservation - Reservation, used for the marker title.
     * @param {{title: string, body: string}} content - InfoWindow content.
     * @returns {Object} Marker.
     */
    createReservationMarker(position, icon, reservation, content) {
      const marker = new this.google.maps.Marker({
        position: position,
        map: this.getTargetMap(this.showReservations),
        title: reservation.booking_number,
        icon: icon
      });
      marker.addListener("click", () => {
        this.openInfoWindow(content, { anchor: marker });
      });
      return marker;
    },
    /**
     * Create the line between origin and destination.
     * @param {{lat: number, lng: number}} from
     * @param {{lat: number, lng: number}} to
     * @returns {Object} Polyline.
     */
    createRouteLine(from, to) {
      return new this.google.maps.Polyline({
        path: [from, to],
        map: this.getTargetMap(this.showReservations),
        strokeColor: ROUTE_LINE_COLOR,
        strokeOpacity: 0.35,
        strokeWeight: 2
      });
    },
    /**
     * Show or hide every reservation overlay according to `showReservations`.
     */
    applyReservationsVisibility() {
      const map = this.getTargetMap(this.showReservations);
      this.reservationOverlays.forEach(overlay => overlay.setMap(map));
    },
    /**
     * Remove every reservation overlay and its listeners.
     */
    clearReservations() {
      this.reservationOverlays.forEach(this.removeOverlay);
      this.reservationOverlays = [];
    },

    /* ---------------------------- Overlays ---------------------------- */

    /**
     * Detach an overlay from the map and drop its listeners.
     * @param {Object} overlay - Polygon, Marker or Polyline.
     */
    removeOverlay(overlay) {
      this.google.maps.event.clearInstanceListeners(overlay);
      overlay.setMap(null);
    },

    /* --------------------------- InfoWindow --------------------------- */

    /**
     * Build the bold, dark title element of the InfoWindow.
     * @param {string} text
     * @returns {HTMLElement}
     */
    createInfoTitle(text) {
      const title = document.createElement("strong");
      title.textContent = text;
      title.style.cssText = INFO_LABEL_STYLE;
      return title;
    },
    /**
     * Set the InfoWindow title and body. Recent Maps versions reserve a header row for
     * the close button, so the title goes there (headerContent) to avoid an empty gap on top.
     * Older versions get the title inline above the body.
     * @param {{title: string, body: string}} content
     */
    setInfoWindowContent(content) {
      const title = this.createInfoTitle(content.title);
      if (typeof this.infoWindow.setHeaderContent === "function") {
        this.infoWindow.setHeaderContent(title);
        this.infoWindow.setContent(content.body);
      } else {
        this.infoWindow.setContent(`${title.outerHTML}${content.body}`);
      }
    },
    /**
     * Open the shared InfoWindow on a marker or at a position.
     * @param {{title: string, body: string}} content
     * @param {{anchor?: Object, position?: Object}} target - Marker to anchor to, or a LatLng.
     */
    openInfoWindow(content, { anchor = null, position = null }) {
      this.setInfoWindowContent(content);
      if (anchor) {
        this.infoWindow.open(this.map, anchor);
      } else {
        this.infoWindow.setPosition(position);
        this.infoWindow.open(this.map);
      }
    }
  }
};
</script>

<style scoped>
.destination-map {
  width: 100%;
  height: 75vh;
  min-height: 480px;
  border-radius: 6px;
}
</style>
